import { JPEG, KEEP, PNG } from './image-upload-config'

// The last step before an image uploads (UppyImageUpload's pre-processor):
// the saved crop is redrawn at exactly the output size and encoded, and only
// that final file is held to the stored limit. Uppy's image editor already
// writes the output size on Save (croppedCanvasOptions), but cropper.js
// rounds a crop box that isn't exactly w:h by a pixel and keeps the source's
// type (a PNG photo stays a large PNG); this makes the stored file exact.

// A JPEG that is over the limit is tried again a little smaller before
// giving up; at 1024x512 the first already fits for nearly any photo.
export const JPEG_QUALITIES = [0.85, 0.75, 0.65, 0.55]

const kb = bytes => Math.max(1, Math.round(bytes / 1024))

// Told to the person when even the smallest encoding is over the limit.
export const tooLargeMessage = (size, maxFileSize) =>
  `The saved picture is ${kb(size)} KB, over the ${kb(maxFileSize)} KB limit. Try a picture with less fine detail.`

export const UNREADABLE = "That picture couldn't be read. Try a JPEG or PNG file."

// A refusal made here, before anything is sent: no status, no reference.
export class ImageOutputError extends Error {
  constructor(message, size) {
    super(message)
    this.name = 'ImageOutputError'
    this.local = true
    this.size = size
  }
}

// The encodings to try, best first: [type, quality].
export const encodeAttempts = ({ output, sourceType, opaque }) => {
  const first = output.quality || JPEG_QUALITIES[0]
  const ladder = [first, ...JPEG_QUALITIES.filter(q => q < first - 1e-9)].map(q => [JPEG, q])
  if (output.type === KEEP && sourceType === PNG) {
    // A PNG keeps its type (a logo's transparency); one that is fully
    // opaque may become a JPEG when the PNG is too large.
    return opaque ? [[PNG, undefined], ...ladder] : [[PNG, undefined]]
  }
  return ladder
}

// Encodes with each attempt until one fits; else refuses with the size.
export const fitToLimit = async ({ attempts, maxFileSize, encode }) => {
  let smallest = null
  for (const [type, quality] of attempts) {
    const blob = await encode(type, quality)
    if (!blob) continue
    if (blob.size <= maxFileSize) return blob
    if (!smallest || blob.size < smallest.size) smallest = blob
  }
  if (!smallest) throw new ImageOutputError(UNREADABLE)
  throw new ImageOutputError(tooLargeMessage(smallest.size, maxFileSize), smallest.size)
}

// Where the source sits in the output: centred, covering it (the crop is
// already w:h, so this only trims a rounding pixel; it never stretches).
export const coverRect = (sourceWidth, sourceHeight, width, height) => {
  const scale = Math.max(width / sourceWidth, height / sourceHeight)
  const w = sourceWidth * scale
  const h = sourceHeight * scale
  return { x: (width - w) / 2, y: (height - h) / 2, w, h }
}

export const isOpaque = pixels => {
  for (let i = 3; i < pixels.length; i += 4) {
    if (pixels[i] < 255) return false
  }
  return true
}

const decode = blob =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve(img)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new ImageOutputError(UNREADABLE))
    }
    img.src = url
  })

const toBlob = (canvas, type, quality) => new Promise(resolve => canvas.toBlob(resolve, type, quality))

const EXTENSIONS = { [JPEG]: 'jpg', [PNG]: 'png' }

// The file's name with its new type's extension (photo.png -> photo.jpg).
export const renamed = (name, type) => {
  const ext = EXTENSIONS[type]
  if (!ext) return name
  const base = (name || 'image').replace(/\.[^./\\]+$/, '')
  return `${base}.${ext}`
}

// The saved crop, redrawn at exactly output.width x output.height and
// encoded to fit maxFileSize. Resolves to a Blob; refuses with an
// ImageOutputError (a message for the person).
export const renderOutput = async (blob, { output, maxFileSize }) => {
  const img = await decode(blob)
  const canvas = document.createElement('canvas')
  canvas.width = output.width
  canvas.height = output.height
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new ImageOutputError(UNREADABLE)
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'

  const keepsPNG = output.type === KEEP && blob.type === PNG
  if (!keepsPNG) {
    // JPEG has no transparency: a transparent area reads white, not black.
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, output.width, output.height)
  }
  const r = coverRect(img.naturalWidth || img.width, img.naturalHeight || img.height, output.width, output.height)
  ctx.drawImage(img, r.x, r.y, r.w, r.h)

  const opaque = keepsPNG ? isOpaque(ctx.getImageData(0, 0, output.width, output.height).data) : true
  const attempts = encodeAttempts({ output, sourceType: blob.type, opaque })
  return fitToLimit({ attempts, maxFileSize, encode: (type, quality) => toBlob(canvas, type, quality) })
}
